import axios from 'axios';
import config from '../config';

const TOKEN_KEY = 'accessToken';
const LEGACY_TOKEN_KEY = 'token';
const REFRESH_BEFORE_EXPIRY_MS = 10 * 60 * 1000;
const ACTIVITY_WINDOW_MS = 15 * 60 * 1000;
const AUTO_REFRESH_POLL_MS = 60 * 1000;

const api = axios.create({
  baseURL: config.backendURL
});

let lastActivityAt = Date.now();
let refreshPromise = null;

function rememberActivity() {
  lastActivityAt = Date.now();
}

function decodeJwtPayload(token) {
  try {
    const base64Url = token.split('.')[1];
    if (!base64Url) return null;

    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );

    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
}

export function getJwtExpMs(token) {
  const payload = decodeJwtPayload(token);
  return payload?.exp ? payload.exp * 1000 : null;
}

function isAuthUrl(url = '') {
  const value = String(url);
  return value.includes('/auth/login') || value.includes('/auth/refresh');
}

function hasRecentActivity() {
  return Date.now() - lastActivityAt <= ACTIVITY_WINDOW_MS;
}

function clearStoredToken() {
  sessionStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(LEGACY_TOKEN_KEY);
  delete api.defaults.headers.common.Authorization;
}

function storeAccessToken(accessToken) {
  sessionStorage.setItem(TOKEN_KEY, accessToken);
  localStorage.setItem(LEGACY_TOKEN_KEY, accessToken);
  api.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
  window.dispatchEvent(new Event('auth:tokenRefresh'));
}

function redirectToLogin() {
  clearStoredToken();
  window.dispatchEvent(new Event('auth:logout'));
  if (window.location.pathname !== '/login') {
    window.location = '/login';
  }
}

async function refreshAccessTokenIfNeeded({ force = false } = {}) {
  const token = sessionStorage.getItem(TOKEN_KEY);
  if (!token) return null;

  const expMs = getJwtExpMs(token);
  if (!expMs) return token;

  const remainingMs = expMs - Date.now();
  if (remainingMs <= 0) {
    redirectToLogin();
    throw new Error('Token ist abgelaufen.');
  }

  if (!force && remainingMs > REFRESH_BEFORE_EXPIRY_MS) {
    return token;
  }

  if (!force && !hasRecentActivity()) {
    return token;
  }

  if (!refreshPromise) {
    refreshPromise = api.post('/auth/refresh', null, { skipAuthRefresh: true })
      .then((response) => {
        const nextToken = response.data?.accessToken || response.data?.token;
        if (!nextToken) throw new Error('Refresh ohne Access Token.');
        storeAccessToken(nextToken);
        return nextToken;
      })
      .catch((error) => {
        redirectToLogin();
        throw error;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }

  return refreshPromise;
}

if (typeof window !== 'undefined') {
  ['click', 'keydown', 'mousemove', 'scroll', 'touchstart'].forEach((eventName) => {
    window.addEventListener(eventName, rememberActivity, { passive: true });
  });
  window.addEventListener('focus', rememberActivity);
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) rememberActivity();
  });

  window.setInterval(() => {
    refreshAccessTokenIfNeeded().catch(() => {});
  }, AUTO_REFRESH_POLL_MS);
}

api.interceptors.request.use(
  async (axiosConfig) => {
    if (!axiosConfig.skipAuthRefresh && !isAuthUrl(axiosConfig.url)) {
      await refreshAccessTokenIfNeeded();
    }

    const token = sessionStorage.getItem(TOKEN_KEY);
    console.log('Verwende Token:', token);
    axiosConfig.headers = axiosConfig.headers || {};
    if (token) {
      axiosConfig.headers.Authorization = `Bearer ${token}`;
    }

    axiosConfig.headers['x-frontend-url'] = config.frontendURL;

    if (axiosConfig.data instanceof FormData) {
      delete axiosConfig.headers['Content-Type'];
    }

    return axiosConfig;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;

    if (status === 401 || status === 403) {
      redirectToLogin();
      return Promise.reject(error);
    }

    if (status === 423) {
      const data = error?.response?.data || {};
      const d = data.details || {};
      let msg = data.error || data.message;

      if (!msg) {
        const parts = ['Aktion nicht moeglich: Kunde ist gesperrt'];

        if (d.blockedUntil) {
          const dtUntil = new Date(d.blockedUntil);
          parts.push(`bis ${dtUntil.toLocaleDateString('de-DE')}`);
        }
        if (d.blockReason) parts.push(`Grund: ${d.blockReason}`);
        if (d.blockedByName) parts.push(`von ${d.blockedByName}`);
        if (d.blockedAt) {
          const dtAt = new Date(d.blockedAt);
          parts.push(`seit ${dtAt.toLocaleDateString('de-DE')}`);
        }

        msg = `${parts.join(' - ')}.`;
      }

      alert(msg);
      return Promise.reject(error);
    }

    return Promise.reject(error);
  }
);

export function calculateRentalPeriodToApi(params) {
  return api.post('/orders/calculateRentalPeriodTo', params);
}

export function patchOrderItemRentalRange(orderId, orderItemId, payload) {
  return api.patch(`/orders/${orderId}/orderitems/${orderItemId}/rental-range`, payload);
}

export function calculateOfferRentalPeriodToApi(params) {
  return api.post('/offers/calculateRentalPeriodTo', params);
}

export function patchOfferItemRentalRange(offerId, offerItemId, payload) {
  return api.patch(`/offers/${offerId}/offeritems/${offerItemId}/rental-range`, payload);
}

export default api;
